/**
 * Choix de la cover d'un défi live (migration 0658) : les 6 images
 * prédéfinies, ou une image importée (envoyée tout de suite dans le dossier de
 * l'utilisateur, `live-covers/<id>/…`). Valeur = chemin dans le bucket.
 */

import { useId, useRef, useState } from 'react';
import { Check, ImagePlus, Loader2 } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  LIVE_COVER_PRESETS,
  UPLOAD_PATH_RE,
  liveCoverUrl,
  presetKeyOfPath,
  presetPath,
  uploadLiveCover,
} from '@/lib/live-covers';

export function LiveCoverPicker({
  value,
  onChange,
  userId,
  label = 'Choisis la cover du défi',
}: {
  value: string | null;
  onChange: (path: string) => void;
  /** Compte réel : peut importer une image (sinon presets seulement). */
  userId: string | null;
  label?: string;
}) {
  const labelId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // La dernière image importée reste proposée même si on revient à un preset.
  const [uploaded, setUploaded] = useState<string | null>(() =>
    value && UPLOAD_PATH_RE.test(value) ? value : null,
  );
  const selectedPreset = presetKeyOfPath(value);

  async function onFile(file: File | undefined) {
    if (!file || !userId) return;
    setError(null);
    setUploading(true);
    try {
      const path = await uploadLiveCover(userId, file);
      setUploaded(path);
      onChange(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'L’image n’a pas pu être envoyée.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-ink-700 text-sm font-semibold" id={labelId}>
        {label}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="grid grid-cols-3 gap-2"
        data-testid="live-cover-picker"
      >
        {LIVE_COVER_PRESETS.map((p) => (
          <CoverTile
            key={p.key}
            label={p.label}
            src={liveCoverUrl({ path: presetPath(p.key), thumb: true })}
            selected={selectedPreset === p.key}
            onSelect={() => onChange(presetPath(p.key))}
          />
        ))}
        {uploaded && (
          <CoverTile
            label="Mon image"
            src={liveCoverUrl({ path: uploaded })}
            selected={value === uploaded}
            onSelect={() => onChange(uploaded)}
          />
        )}
        {userId && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="border-ink-300 text-ink-600 hover:border-primary-400 hover:text-primary-600 flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed text-xs font-semibold transition active:scale-[0.98] disabled:opacity-60"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <ImagePlus className="h-5 w-5" aria-hidden />
            )}
            {uploading ? 'Envoi…' : uploaded ? 'Autre image' : 'Importer'}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label="Importer une image de cover"
        onChange={(e) => void onFile(e.target.files?.[0])}
      />
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </div>
  );
}

function CoverTile({
  label,
  src,
  selected,
  onSelect,
}: {
  label: string;
  src: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      className={cn(
        'relative aspect-[4/3] overflow-hidden rounded-xl bg-[#061433] transition active:scale-[0.98]',
        selected
          ? 'ring-[3px] ring-[#f4c64e] ring-offset-2 ring-offset-transparent'
          : 'ring-1 ring-black/10',
      )}
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
      />
      <span className="bg-linear-to-t absolute inset-x-0 bottom-0 from-black/70 to-transparent px-1.5 pb-1 pt-4 text-left text-xs font-semibold leading-tight text-white">
        {label}
      </span>
      {selected && (
        <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#f4c64e] text-[#0a2e57] shadow">
          <Check className="h-4 w-4" aria-hidden />
        </span>
      )}
    </button>
  );
}
